import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";

function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  danger = false,
  busy = false,
  onConfirm,
  onClose,
}) {
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--border-color)",
          backgroundColor: "var(--bg-elevated)",
          boxShadow: "var(--shadow-medium)",
          backgroundImage: "none",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, color: "var(--text-main)", fontWeight: 700 }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: "var(--text-muted)" }}>
          {message}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={danger ? "error" : "primary"}
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? "Working…" : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Confirm-dialog state helper.
 * `ask({ title, message, confirmLabel, action, danger })` opens the dialog;
 * `action` runs on confirm, errors are passed to `onError`.
 * Render the returned `dialog` element once in the component tree.
 */
export function useConfirmDialog(onError) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const ask = (cfg) => setState(cfg);

  const dialog = (
    <ConfirmDialog
      open={!!state}
      danger={state?.danger !== false}
      title={state?.title || ""}
      message={state?.message || ""}
      confirmLabel={state?.confirmLabel || "Confirm"}
      busy={busy}
      onClose={() => setState(null)}
      onConfirm={async () => {
        setBusy(true);
        try {
          await state.action();
          setState(null);
        } catch (e) {
          onError?.(e.message || "Something went wrong");
        } finally {
          setBusy(false);
        }
      }}
    />
  );

  return [ask, dialog];
}

export default ConfirmDialog;
